import type { Hole } from '../course/Hole';

export type RoundStatus = 'playing' | 'hole-complete' | 'round-complete';

export interface HoleScore {
  readonly holeNumber: number;
  readonly par: number;
  readonly yardage: number;
  strokes: number;
  completed: boolean;
}

export interface RoundCompetitorState {
  readonly id: string;
  readonly displayName: string;
  readonly kind: 'player' | 'ai';
  visibleBall: boolean;
  activeHoleNumber: number;
  strokesByHole: Map<number, number>;
}

export class RoundScore {
  readonly holes: HoleScore[];
  readonly competitors: RoundCompetitorState[];
  private activeIndex = 0;
  private statusValue: RoundStatus = 'playing';

  constructor(holes: readonly Hole[], competitors: RoundCompetitorState[] = []) {
    this.holes = holes.map((hole) => ({
      holeNumber: hole.number,
      par: hole.par,
      yardage: hole.yardage,
      strokes: 0,
      completed: false,
    }));
    if (this.holes.length === 0) throw new Error('RoundScore requires at least one hole');
    this.competitors = competitors;
  }

  frontNinePar(): number {
    return this.holes.slice(0, 9).reduce((sum, hole) => sum + hole.par, 0);
  }

  backNinePar(): number {
    return this.holes.slice(9, 18).reduce((sum, hole) => sum + hole.par, 0);
  }

  frontNineStrokes(): number {
    return this.holes.slice(0, 9).reduce((sum, hole) => sum + hole.strokes, 0);
  }

  backNineStrokes(): number {
    return this.holes.slice(9, 18).reduce((sum, hole) => sum + hole.strokes, 0);
  }

  get status(): RoundStatus {
    return this.statusValue;
  }

  get activeHoleNumber(): number {
    return this.activeHole.holeNumber;
  }

  get activeHole(): HoleScore {
    return this.holes[this.activeIndex];
  }

  get isFinalHole(): boolean {
    return this.activeIndex === this.holes.length - 1;
  }

  recordStroke(): void {
    if (this.statusValue !== 'playing') return;
    this.activeHole.strokes += 1;
  }

  completeActiveHole(): void {
    this.activeHole.completed = true;
    this.statusValue = this.isFinalHole ? 'round-complete' : 'hole-complete';
  }

  advanceToNextHole(): number | null {
    if (this.statusValue !== 'hole-complete' || this.isFinalHole) return null;
    this.activeIndex += 1;
    this.statusValue = 'playing';
    return this.activeHoleNumber;
  }

  restartHole(holeNumber = this.activeHoleNumber): void {
    const score = this.holes.find((hole) => hole.holeNumber === holeNumber);
    if (!score) return;
    score.strokes = 0;
    score.completed = false;
    if (holeNumber === this.activeHoleNumber) this.statusValue = 'playing';
  }

  restartRound(): void {
    this.holes.forEach((hole) => {
      hole.strokes = 0;
      hole.completed = false;
    });
    this.activeIndex = 0;
    this.statusValue = 'playing';
  }

  relativeToPar(hole: HoleScore): number {
    return hole.strokes - hole.par;
  }

  totalStrokes(): number {
    return this.holes.reduce((sum, hole) => sum + hole.strokes, 0);
  }

  totalPar(): number {
    return this.holes.reduce((sum, hole) => sum + hole.par, 0);
  }

  completedTotalRelativeToPar(): number {
    return this.holes.reduce(
      (sum, hole) => sum + (hole.completed ? this.relativeToPar(hole) : 0),
      0,
    );
  }

  resultLabel(hole: HoleScore): string {
    const relative = this.relativeToPar(hole);
    if (relative <= -2) return 'Eagle';
    if (relative === -1) return 'Birdie';
    if (relative === 0) return 'Par';
    if (relative === 1) return 'Bogey';
    if (relative === 2) return 'Double Bogey';
    return `+${relative}`;
  }
}
